import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, Check, AlertCircle, Plus, Minus } from "lucide-react";
import {
  normalizeValue,
  computeOptionExtras,
  formatDelta,
  isGroupIncluded,
  isGroupRequired,
} from "../lib/menuOptions";

/**
 * Modal that opens when adding an item that has `options` (e.g. wings sauces).
 *
 * Two selection modes:
 *   • Single-set (legacy) — one modifier set applies to the whole quantity.
 *     Used by the admin OrderEditorModal.
 *   • Per-pan (opt-in via `perPanEnabled`) — every pan/dozen gets its OWN
 *     modifier choices. When the buyer picks 3 half-pans of wings they can
 *     do Buffalo + BBQ + Lemon Pepper instead of one sauce for all three.
 *
 * onConfirm receives:
 *   { selectedOptions, quantity, perPanSelections }
 * where `perPanSelections` is a length-`quantity` array of per-pan option
 * objects. Callers that want the split-into-N-lines behavior should iterate
 * `perPanSelections` and call addItem() once per entry.
 */
export const OptionPickerModal = ({
  item,
  isOpen,
  onClose,
  onConfirm,
  initialSelections,
  initialQuantity,
  perPanEnabled = false,
}) => {
  const [qty, setQty] = useState(1);
  // Array of per-pan selection dicts. Length always tracks `qty` when the
  // buyer is in per-pan mode. Even in legacy single-set mode we keep a
  // one-element array here and mirror it into every pan on confirm — the
  // internal state stays simple.
  const [panSelections, setPanSelections] = useState([{}]);
  const [error, setError] = useState("");

  const optionGroups = useMemo(() => item?.options || [], [item]);

  // Reset whenever a new item opens the modal.
  useEffect(() => {
    if (!isOpen) return;
    const nextQty = Math.max(1, Number(initialQuantity) || 1);
    setQty(nextQty);
    setError("");
    const seed = {};
    for (const grp of item?.options || []) {
      const prev = initialSelections?.[grp.label];
      seed[grp.label] = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
    }
    // Every pan starts with the same seed (usually empty on first open).
    setPanSelections(Array.from({ length: nextQty }, () => cloneSel(seed)));
    // Intentional: seed only when the picker opens, so edits aren't overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item]);

  // Keep panSelections length in sync with qty as the user increments /
  // decrements the "How many?" stepper. Growing keeps existing entries.
  useEffect(() => {
    setPanSelections((prev) => {
      if (prev.length === qty) return prev;
      if (prev.length < qty) {
        const seed = {};
        for (const grp of optionGroups) seed[grp.label] = [];
        return [
          ...prev,
          ...Array.from({ length: qty - prev.length }, () => cloneSel(seed)),
        ];
      }
      return prev.slice(0, qty);
    });
  }, [qty, optionGroups]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const showPerPan = perPanEnabled && qty >= 2;

  // Sum of per-pan modifier upcharges — respects per-pan choices when in
  // per-pan mode, otherwise mirrors pan 0 × qty.
  const extrasTotal = useMemo(() => {
    if (showPerPan) {
      return panSelections.reduce(
        (sum, sel) => sum + computeOptionExtras(item, sel),
        0,
      );
    }
    return computeOptionExtras(item, panSelections[0] || {}) * qty;
  }, [item, panSelections, qty, showPerPan]);

  const togglePan = (panIndex, group, label) => {
    setError("");
    setPanSelections((prev) => {
      const next = prev.map((sel, i) => (i === panIndex ? { ...sel } : sel));
      const current = next[panIndex][group.label] || [];
      const has = current.includes(label);
      if (has) {
        next[panIndex][group.label] = current.filter((v) => v !== label);
      } else {
        const max = group.max_selections || 1;
        if (current.length >= max) {
          next[panIndex][group.label] =
            max === 1 ? [label] : [...current.slice(1), label];
        } else {
          next[panIndex][group.label] = [...current, label];
        }
      }
      return next;
    });
  };

  const handleConfirm = () => {
    // Validate every pan in scope. In legacy mode there's only 1 to check.
    const scope = showPerPan ? panSelections : panSelections.slice(0, 1);
    for (let i = 0; i < scope.length; i += 1) {
      for (const grp of optionGroups) {
        const picks = scope[i]?.[grp.label] || [];
        // Honor both the plain `required: true` and the conditional
        // `required_when: {"Parent Group": ["value"]}` so items like the
        // Chopped Cheese slider that MUST be paired with a cheese pick
        // are gated at confirm time.
        if (isGroupRequired(grp, scope[i]) && picks.length === 0) {
          setError(
            showPerPan
              ? `Pan ${i + 1}: please pick at least one ${grp.label.toLowerCase()}.`
              : `Please pick at least one ${grp.label.toLowerCase()}.`,
          );
          return;
        }
      }
    }
    const finalPerPan = showPerPan
      ? panSelections
      : Array.from({ length: qty }, () => cloneSel(panSelections[0] || {}));
    onConfirm({
      // Back-compat single-set (== pan 0). Callers that don't split lines
      // can still use this and get today's behavior.
      selectedOptions: panSelections[0] || {},
      quantity: qty,
      // New — one entry per pan. Callers that want per-pan lines iterate
      // this list and call addItem() once per entry.
      perPanSelections: finalPerPan,
    });
  };

  if (!isOpen || !item) return null;

  const priceLabel =
    item.price_max && item.price_max !== item.price
      ? `$${item.price.toFixed(2)} – $${item.price_max.toFixed(2)}`
      : `$${item.price.toFixed(2)}`;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        data-testid="option-modal-backdrop"
        className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm animate-in fade-in"
      />
      {/* Modal */}
      <div
        data-testid="option-modal"
        role="dialog"
        aria-modal="true"
        className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:max-w-md w-auto sm:w-full z-[90] bg-white rounded-3xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)] overflow-hidden max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <header className="relative shrink-0">
          {item.image ? (
            <div className="h-40 sm:h-44 bg-brand-cream overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="h-full w-full object-cover"
              />
            </div>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            data-testid="option-modal-close"
            aria-label="Close"
            className="absolute top-3 right-3 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-md text-brand-charcoal"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-display font-black text-[18px] sm:text-[20px] text-brand-charcoal tracking-tight leading-tight">
              {item.name}
            </h3>
            <span className="shrink-0 rounded-full bg-brand-cream text-brand-charcoal px-2.5 py-1 text-[12px] font-black tabular-nums">
              {priceLabel}
            </span>
          </div>
          {item.description ? (
            <p className="mt-2 text-[12.5px] text-brand-charcoal/65 leading-snug">
              {item.description}
            </p>
          ) : null}

          {showPerPan ? (
            <p
              data-testid="option-modal-per-pan-hint"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-teal/10 text-brand-teal px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em]"
            >
              Pick modifiers · one per pan
            </p>
          ) : null}

          {/* Option groups */}
          <div className="mt-5 space-y-6">
            {(showPerPan ? panSelections : panSelections.slice(0, 1)).map(
              (panSel, panIdx) => (
                <PanBlock
                  key={panIdx}
                  panIndex={panIdx}
                  showPanLabel={showPerPan}
                  panSelections={panSel}
                  optionGroups={optionGroups}
                  onToggle={(grp, label) => togglePan(panIdx, grp, label)}
                />
              ),
            )}
          </div>

          {error ? (
            <p
              data-testid="option-modal-error"
              role="alert"
              className="mt-4 inline-flex items-center gap-2 text-[12px] text-red-600"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              {error}
            </p>
          ) : null}
        </div>

        {/* Qty + confirm — sticky footer so the CTA stays visible */}
        <div className="px-5 sm:px-6 py-4 border-t border-black/5 bg-white shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex items-center gap-1 rounded-full bg-brand-cream border border-black/5 p-1">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                aria-label="Decrease quantity"
                data-testid="option-modal-qty-dec"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-white text-brand-charcoal"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span
                data-testid="option-modal-qty-value"
                className="min-w-[24px] text-center text-[14px] font-bold tabular-nums"
              >
                {qty}
              </span>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(50, q + 1))}
                aria-label="Increase quantity"
                data-testid="option-modal-qty-inc"
                className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-white text-brand-charcoal"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleConfirm}
              data-testid="option-modal-confirm"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
            >
              Add to Cart · ${(item.price * qty + extrasTotal).toFixed(2)}
              {extrasTotal > 0 ? (
                <span
                  data-testid="option-modal-extras"
                  className="text-[10px] font-bold tracking-[0.08em] normal-case text-brand-gold"
                >
                  (incl. +${extrasTotal.toFixed(2)})
                </span>
              ) : null}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

/** Deep-ish clone of a per-pan selections dict (values are string arrays). */
function cloneSel(sel) {
  const out = {};
  for (const k of Object.keys(sel || {})) {
    out[k] = Array.isArray(sel[k]) ? [...sel[k]] : sel[k];
  }
  return out;
}

const PanBlock = ({
  panIndex,
  showPanLabel,
  panSelections,
  optionGroups,
  onToggle,
}) => (
  <div
    data-testid={`option-pan-${panIndex}`}
    className={
      showPanLabel
        ? "rounded-2xl border-2 border-brand-cream bg-brand-cream/40 px-4 py-4"
        : ""
    }
  >
    {showPanLabel ? (
      <p className="text-[11.5px] font-bold uppercase tracking-[0.16em] text-brand-teal">
        Pan {panIndex + 1}
      </p>
    ) : null}
    <div className={`space-y-4 ${showPanLabel ? "mt-3" : ""}`}>
      {optionGroups.map((grp) => {
        const picks = panSelections?.[grp.label] || [];
        // "Included" — parent choice already covers this modifier's cost.
        const included = isGroupIncluded(grp, panSelections);
        // Chopped Cheese-style: required only when a parent value triggers it.
        const isRequired = isGroupRequired(grp, panSelections);
        return (
          <div key={grp.label}>
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-charcoal">
                {grp.label}
                {isRequired ? (
                  <span className="text-brand-red ml-1">*</span>
                ) : null}
                {included ? (
                  <span
                    data-testid={`option-group-included-${grp.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="ml-2 inline-flex items-center rounded-full bg-brand-teal/10 text-brand-teal px-2 py-0.5 text-[9.5px] font-black uppercase tracking-[0.14em] normal-case"
                  >
                    Included
                  </span>
                ) : null}
              </p>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/45 shrink-0">
                {picks.length}/{grp.max_selections || 1} chosen
              </p>
            </div>
            {grp.help ? (
              <p className="mt-1 text-[11.5px] text-brand-charcoal/55">
                {grp.help}
              </p>
            ) : null}
            <div
              data-testid={`option-group-${panIndex}-${grp.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="mt-2.5 flex flex-wrap gap-2"
            >
              {grp.values.map((rawValue) => {
                const v = normalizeValue(rawValue);
                const active = picks.includes(v.label);
                // When the group is "included" by a parent choice, don't
                // show the +$X delta on chips — swap it for a subtle
                // "Included" hint on the active chip. Non-active chips
                // just drop the delta so they don't advertise the wrong
                // price.
                const deltaLabel = included ? "" : formatDelta(v.price_delta);
                return (
                  <button
                    key={v.label}
                    type="button"
                    onClick={() => onToggle(grp, v.label)}
                    data-testid={`option-value-${panIndex}-${v.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-bold transition-colors ${
                      active
                        ? "bg-brand-charcoal text-white border-brand-charcoal"
                        : "bg-white text-brand-charcoal border-black/15 hover:border-brand-charcoal"
                    }`}
                  >
                    {active ? <Check className="h-3 w-3" /> : null}
                    <span>{v.label}</span>
                    {deltaLabel ? (
                      <span
                        className={`tabular-nums ${active ? "text-brand-gold" : "text-brand-teal"}`}
                      >
                        {deltaLabel}
                      </span>
                    ) : null}
                    {included && active ? (
                      <span className="text-[9.5px] font-black uppercase tracking-[0.14em] text-brand-gold">
                        Free
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default OptionPickerModal;
